import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { Prose } from '@/components/prose';
import { EmptyState, buttonClass, inputClass, textareaClass } from '@/components/ui';
import { cn } from '@/lib/cn';
import { adminHelpQuery, helpArticleQuery, helpSearchQuery } from '@/queries';

/**
 * Help. The manual, in the application.
 *
 * Guy, Kat and Jade learned this system by being shown it. That does not scale
 * past three people and does not survive anybody leaving, so the fourth person
 * needs somewhere to read instead of somebody's afternoon.
 *
 * Two ways in, because people arrive with two different questions:
 *
 *   - "How do I do X" goes in the search box. Postgres ranks a title match
 *     above a passing mention, and the result carries the sentence the words
 *     were found in, so the reader can tell the right page from the near miss
 *     without opening either.
 *   - "What is there" goes to the contents, grouped by section.
 *
 * The articles are rows in a table. The text can be corrected the moment
 * somebody spots it is wrong, which is the only way a manual stays true.
 */

/**
 * Strip the markdown out of a snippet.
 *
 * `ts_headline` reads the raw body, so a result read
 * `## The three numbers - **On hand** is what is [physically](/admin/stock)`,
 * which is the source rather than the sentence. A reader scanning results
 * should see prose.
 *
 * Done here rather than by storing a plain text copy beside the body: two
 * copies of the same words drift, and the one nobody looks at drifts first.
 * The `<<>>` markers survive, because they are what the highlighting uses.
 */
function plain(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')   // [words](/path) -> words
    .replace(/^#{1,6}\s*/gm, '')                 // leading heading marks
    .replace(/#{2,6}\s+/g, '')                   // heading marks mid snippet
    .replace(/\*\*([^*]+)\*\*/g, '$1')          // **bold** -> bold
    .replace(/`([^`]+)`/g, '$1')                  // `code` -> code
    .replace(/^\s*[-*]\s+/gm, '')                // bullet marks
    // `ts_headline` cuts wherever the match is, which lands mid `**bold**`
    // often enough to be visible. Anything left over is a stray mark rather
    // than emphasis, so it goes.
    .replace(/\*+/g, '')
    .replace(/#+/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Turn the `<<match>>` markers `ts_headline` returns into highlights. */
function Snippet({ text }: { text: string }) {
  const parts = plain(text).split(/(<<[^>]*>>)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('<<') && part.endsWith('>>') ? (
          <mark key={i} className="rounded-[3px] bg-brand/30 px-0.5 text-ink">
            {part.slice(2, -2)}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ))}
    </>
  );
}

/**
 * Edit a page, in place.
 *
 * The client's reason for putting the manual in a table was so it can be
 * corrected without a release. A table nobody can edit from the application is
 * the same gap as the five others found this week, so the editor ships with
 * the reader rather than after it.
 *
 * Markdown, in a plain box. The preview is the page itself: save and read it.
 * A live preview beside the text would be a second renderer to keep in step
 * with the first.
 */
function Editor({ article, onDone }: {
  article: { slug: string; title: string; section: string; summary: string; body: string };
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(article);
  const [said, setSaid] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/help', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not save it.');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'help'] });
      onDone();
    },
    onError: (e: Error) => setSaid(e.message),
  });

  return (
    <form
      className="mt-5 border-t border-line pt-5"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Title</span>
          <input
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className={`${inputClass} w-full`}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Section</span>
          <input
            value={draft.section}
            onChange={(e) => setDraft((d) => ({ ...d, section: e.target.value }))}
            className={`${inputClass} w-full`}
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="mb-1 block text-[12.5px] font-semibold">Summary</span>
        <input
          value={draft.summary}
          onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
          className={`${inputClass} w-full`}
        />
        <span className="mt-1 block text-[12px] text-muted">
          One sentence. It is what somebody reads in a search result.
        </span>
      </label>

      <label className="mt-3 block">
        <span className="mb-1 block text-[12.5px] font-semibold">The page</span>
        <textarea
          value={draft.body}
          onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
          rows={20}
          className={`${textareaClass} w-full resize-y font-[family-name:var(--font-mono)] text-[13px]`}
        />
        <span className="mt-1 block text-[12px] text-muted">
          <code className="font-[family-name:var(--font-mono)]">##</code> for a heading,
          <code className="font-[family-name:var(--font-mono)]">1.</code> for a step,
          <code className="font-[family-name:var(--font-mono)]">-</code> for a point, and
          <code className="font-[family-name:var(--font-mono)]">[words](/admin/stock)</code> for a link.
        </span>
      </label>

      {said && (
        <p className="mt-3 rounded-[10px] border border-line p-3 text-[13px]">{said}</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="submit" disabled={save.isPending} className={buttonClass('primary', 'sm')}>
          {save.isPending ? 'Saving...' : 'Save the page'}
        </button>
        <button type="button" onClick={onDone} className={buttonClass('secondary', 'sm')}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminHelpPage() {
  const { articles } = useSuspenseQuery(adminHelpQuery).data;
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { page?: string };

  /**
   * The open page lives in the URL, not in state.
   *
   * That is what makes a cross link work and a help page shareable. The first
   * article is the default, so arriving with no page still shows something.
   */
  const slug = search.page ?? articles[0]?.slug ?? null;
  const setSlug = (next: string) => {
    void navigate({ to: '/admin/help', search: { page: next } });
  };
  const [term, setTerm] = useState('');
  const [editing, setEditing] = useState(false);
  const [needle, setNeedle] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setNeedle(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data: found } = useQuery({
    ...helpSearchQuery(needle),
    placeholderData: keepPreviousData,
  });
  const { data: open } = useQuery(helpArticleQuery(slug));

  /** The contents, grouped, in the order the sections were given. */
  const sections = useMemo(() => {
    const map = new Map<string, typeof articles>();
    for (const a of articles) map.set(a.section, [...(map.get(a.section) ?? []), a]);
    return [...map.entries()];
  }, [articles]);

  const results = found?.results ?? [];
  const searching = needle.length >= 2;

  if (articles.length === 0) {
    return (
      <div>
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Help</h1>
        <div className="mt-5">
          <EmptyState
            title="The manual has not been written yet"
            body="Help pages are rows in a table, so they can be written and corrected
                  without a release. Once there are some, this is where anybody new
                  reads them."
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-[14px]">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Help</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          How this system works, screen by screen. Search it, or read through
          the contents.
        </p>
      </div>

      <input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search the manual. Try stocktake, invoice, or reorder point"
        aria-label="Search the manual"
        className={cn(inputClass, 'mb-6 h-10 w-full text-[14px]')}
      />

      {searching ? (
        <section aria-live="polite">
          <h2 className="mb-1 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
            {results.length === 0
              ? `Nothing matches ${needle}`
              : `${results.length} result${results.length === 1 ? '' : 's'} for ${needle}`}
          </h2>

          {/*
            * Say when the search widened.
            *
            * "stock take" finds nothing on an exact match, because the page
            * says "stocktake" as one word. Rather than show an empty screen
            * the search retries with any of the words, and this line is what
            * stops the looser results reading as exact ones.
            */}
          {results.length > 0 && results[0]?.loose && (
            <p className="mb-3 text-[12.5px] text-muted">
              No page has all of those words together, so these match any of them.
            </p>
          )}

          {results.length === 0 ? (
            <EmptyState
              title="No page mentions that"
              body="Try a different word, or read through the contents below. If the
                    manual is missing something, tell the studio and it gets added."
            />
          ) : (
            <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
              {results.map((r) => (
                <button
                  key={r.slug}
                  type="button"
                  onClick={() => { setSlug(r.slug); setTerm(''); }}
                  className="block w-full border-b border-line px-4 py-3 text-left last:border-0 hover:bg-raised"
                >
                  <span className="block text-[14px] font-semibold">{r.title}</span>
                  <span className="mt-0.5 block text-[12px] uppercase tracking-[0.06em] text-muted">
                    {r.section}
                  </span>
                  <span className="mt-1 block text-[13px] text-muted">
                    <Snippet text={r.snippet} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
          <nav aria-label="Contents">
            {sections.map(([section, items]) => (
              <div key={section} className="mb-5">
                <h2 className="mb-2 text-[12px] font-bold uppercase tracking-[0.08em] text-muted">
                  {section}
                </h2>
                <ul className="space-y-0.5">
                  {items.map((a) => (
                    <li key={a.slug}>
                      <button
                        type="button"
                        onClick={() => { setSlug(a.slug); setEditing(false); }}
                        className={cn(
                          'w-full rounded-[6px] px-2 py-1.5 text-left text-[13.5px]',
                          a.slug === slug
                            ? 'bg-ink font-semibold text-[color:var(--color-panel)]'
                            : 'text-ink hover:bg-raised',
                        )}
                      >
                        {a.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <article className="min-w-0 rounded-[10px] border border-line bg-panel p-6">
            {open?.article ? (
              <>
                <p className="text-[12px] uppercase tracking-[0.06em] text-muted">
                  {open.article.section}
                </p>
                <h2 className="mt-1 text-[20px] font-semibold tracking-[-0.01em]">
                  {open.article.title}
                </h2>
                <p className="mt-1 text-[13px] text-muted">{open.article.summary}</p>
                {editing ? (
                  <Editor article={open.article} onDone={() => setEditing(false)} />
                ) : (
                  <>
                    <div className="mt-5 border-t border-line pt-5">
                      <Prose source={open.article.body} />
                    </div>
                    {/* Anybody without settings.edit gets a refusal from the
                        server, so this is a convenience rather than the
                        boundary. */}
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className={buttonClass('secondary', 'sm', 'mt-6')}
                    >
                      Edit this page
                    </button>
                  </>
                )}
              </>
            ) : (
              <p className="text-[14px] text-muted">Choose a page from the contents.</p>
            )}
          </article>
        </div>
      )}
    </div>
  );
}
